<script lang="ts">
import { computed, provide } from 'vue';

import { PhxsgIconIds, PhxsgIcon, type PhxsgIconSize } from '@sg/src/View/Components/Atoms/Icon';
import { PhxsgButtonProps, PhxsgButton as PhxsgCoreButton } from '@sg-core/src/View/Components/Atoms/Button';
import { PhxsgLoaderProps } from '@sg-core/src/View/Components/Atoms/Loader';
import '@sg/src/View/Components/Atoms/Loader/loader.css';

export type AlsButtonVariant = 'primary' | 'secondary' | 'link';

export type AlsPhxsgButtonProps = Omit<PhxsgButtonProps, 'variant'> & {
  variant?: AlsButtonVariant;
  iconId?: PhxsgIconIds;
  iconSize?: PhxsgIconSize;
  iconPosition?: 'start' | 'end';
};
</script>

<script setup lang="ts">
const {
  variant,
  iconId,
  iconSize,
  iconPosition = 'start',
  icon,
  iconOnly,
  ...buttonProps
} = defineProps<AlsPhxsgButtonProps>();

const btnIcon = computed(() => (iconId ? { id: iconId, size: iconSize } : null));
const computedIcon = computed(() => {
  if (icon) {
    return icon;
  }

  if (btnIcon.value && !iconOnly) {
    if (iconPosition === 'end') {
      return { end: btnIcon.value };
    }

    return { start: btnIcon.value };
  }

  return undefined;
});

const computedLoaderProps = computed((): PhxsgLoaderProps => ({
  type: 'circle',
  ...(buttonProps.loaderProps ?? {}),
}));

provide('PhxsgIcon', PhxsgIcon);
</script>

<template>
  <PhxsgCoreButton
    v-bind="buttonProps"
    :loader-props="computedLoaderProps"
    :icon-only="iconOnly"
    :icon="computedIcon"
    :variant="variant"
  >
    <slot />

    <template v-if="$slots.content || iconOnly" #content>
      <slot name="content">
        <PhxsgIcon v-if="iconOnly && btnIcon" v-bind="btnIcon" :class="{ 'max-lg:icon--sm': !iconSize }" />
      </slot>
    </template>
  </PhxsgCoreButton>
</template>

<style src="./button.css"></style>
